/**
 * `src/platform/untrusted-text.ts`: the console helpers that make untrusted text safe to show
 * (plan 2026-10-22-console-cleanup-g8, #2219, #2230).
 *
 * Untrusted text is any string the console did not write. The helpers cover these classes (R-4, R-12):
 *
 * - {@link displayUntrusted}: error text and log lines. Cap, layout, escape, redact.
 * - {@link displayUntrustedName}: a name or a display name. Cap 256, layout and escape, with no
 *   redaction: a false hit in a name hides which object the row is.
 * - {@link displayUntrustedDescription}: a description. The same steps, with the cap
 *   {@link UNTRUSTED_TEXT_MAX_CHARS}.
 * - {@link displayUntrustedField}: one key and value of a structured log line. A secret key hides
 *   its value whole.
 *
 * The steps, in this order:
 *
 * 1. Cap. It keeps the first `maxChars` code points, so a surrogate pair stays whole, and adds
 *    {@link untrustedTextMarker}. The cap runs first, so a large body never reaches a pattern.
 * 2. Layout. On one line each run of `\n`, `\r` and `\t` becomes one space. With `multiline: true`
 *    `\n` and `\t` stay and `\r` becomes nothing. A removed `\r` cannot join a secret after a look.
 * 3. Escape. Each control, bidi, invisible or blank-looking code point becomes the visible text
 *    `<U+XXXX>` (upper-case hex, 4 to 6 digits). The helper never strips: a strip hides an attack.
 * 4. Redact ({@link displayUntrusted} only). Each secret shape of {@link REDACTION_RULES} and of the
 *    self-named rules becomes {@link REDACTED_TEXT}. Redaction reads escaped text, so a first and a
 *    second pass see the same characters. For the pair rules P2 and P7, an invisible code point inside
 *    a secret cannot end the value early, because their value guard skips `<U+XXXX>` tokens. The
 *    self-named rules read the visible view, which drops each `<U+XXXX>` token.
 *
 * ## The only control for most text, a second layer for two
 *
 * This is a blocklist in the browser. Two classes also pass a server redactor, so this helper is a
 * second layer there: `lastError` (`redactSecrets` in provisioning-svc) and CP service logs
 * (`ScrubbingLoggerService`). Names, descriptions, rule names, label values, envelope errors and
 * most server error text pass no server control (#2219, #2230), so on the console read path this
 * helper is their only control. Residual risks:
 *
 * - A secret in a shape the rules do not list passes.
 * - A 64-character hex session token passes the opaque-token rule (P11), which keeps hex runs so a
 *   git sha and an image digest stay readable.
 * - A lowercase kebab or snake secret (`a1b2-c3d4-...`) passes P11, which keeps joined identifiers.
 * - A secret with no digit passes P11, which keeps words and names.
 * - A secret of 40 or more characters that holds a UUID with 16 or fewer characters on each side
 *   passes P11, which keeps prefixed identifiers such as `org_<uuid>`.
 * - A lowercase kebab value after the bare key `secret` passes P7, which keeps Kubernetes Secret names.
 * - A URI password that holds a raw `/` passes the userinfo rule (P8): the authority ends at `/`, so a
 *   path such as `http://registry:4873/@scope/pkg` stays. A password that holds `@` or `:`, and an empty
 *   user, go (review row WS-14-P3-R27).
 * - In P11 an invisible code point inside a secret ends the match at its `<U+XXXX>` token, so the
 *   part of the secret after the token can show. The self-named rules P1, P3, P4, P5 and P6 read the
 *   visible view of `visible-text.ts`, so an invisible code point anywhere in such a secret, its
 *   prefix included, cannot split it (review rows WS-14-P2-R25, R32 and R42).
 * - A secret that the cap cuts below a rule's floor stays visible as a fragment.
 *
 * ## One pass per surface (R-6)
 *
 * The cap reads code points only, so text that already looks like a marker is plain text, and an
 * author cannot spoof one. Escape and redaction are idempotent: a second pass over output that fits
 * the cap returns it. `ErrorState` and the log column use {@link UNTRUSTED_BLOCK_MAX_CHARS}, so a
 * second pass over `displayMessage` output never cuts.
 */

import { matchRanges, replaceVisibleRanges, type VisibleBoundaries, type VisibleRange } from './visible-text';

/** The default cap, in characters. The number `ENVELOPE_ERROR_DETAIL_MAX_CHARS` uses. */
export const UNTRUSTED_TEXT_MAX_CHARS = 4000;

/**
 * The cap of a block surface: the `ErrorState` body and a log line. 4000 escaped code points of
 * up to 9 characters each, plus the marker, stay under it, so a second pass never cuts.
 */
export const UNTRUSTED_BLOCK_MAX_CHARS = 65536;

/** The text that takes the place of each redacted secret. */
export const REDACTED_TEXT = '[redacted]';

/** The cap of {@link displayUntrustedName}, in characters: the longest group name cp-auth-api takes. */
const NAME_MAX_CHARS = 256;

/** The options of {@link displayUntrustedName}. */
interface DisplayUntrustedNameOptions {
  /** The cap in characters, a positive integer. Default 256. */
  readonly maxChars?: number;
}

/** One key and value of a log line, both ready to show. */
export interface DisplayedField {
  readonly key: string;
  readonly value: string;
}

/** The options of {@link displayUntrusted}. */
interface DisplayUntrustedOptions {
  /** The cap in characters, a positive integer. Default {@link UNTRUSTED_TEXT_MAX_CHARS}. */
  readonly maxChars?: number;
  /** Keep `\n` and `\t` for a multi-line surface. Default `false`: each layout run is one space. */
  readonly multiline?: boolean;
}

/**
 * The suffix a cut text ends with: ` [Shortened for display, not by the platform: 4000 of 9120
 * characters shown]`. It shares its head with `ENVELOPE_ERROR_TRUNCATION_MARKER` of
 * `envelope-error-text.ts` and adds its own count. No ellipsis glyph.
 */
function untrustedTextMarker(shown: number, total: number): string {
  return ` [Shortened for display, not by the platform: ${shown} of ${total} characters shown]`;
}

/**
 * Show error text or a log line in the console: cap it, make every control and bidi code point
 * visible, redact secret shapes, and say when it was cut.
 *
 * Callers: `displayMessage`, `apiErrorMessage`, both `ErrorState` bodies, the envelope error cell,
 * `logMessageColumn`, the raw line of `AppLogRowDetail` and {@link displayUntrustedField}.
 *
 * Input: any string, the empty string included. Output: display text with no code point of the
 * escape class except the emoji carve-outs and, with `multiline: true`, `\n` and `\t`; every
 * listed secret shape as {@link REDACTED_TEXT}; and at most `maxChars` code points of the source before the marker. A text that fits has no marker.
 *
 * Errors: a `maxChars` that is not a positive integer throws a `RangeError` (a programmer error).
 *
 * Pure: no I/O, no global state. A second pass over output that fits the cap returns it.
 */
export function displayUntrusted(text: string, options: DisplayUntrustedOptions = {}): string {
  const { body, marker } = cap(text, checkedCap('displayUntrusted', options.maxChars ?? UNTRUSTED_TEXT_MAX_CHARS));
  const laid = options.multiline === true ? body.replace(/\r/g, '') : oneLine(body);
  return redactSecrets(escapeInvisible(laid, false)) + marker;
}

/**
 * Show an author name or display name in the console: cap it (256 code points by default, the
 * longest group name cp-auth-api takes), put it on one line and make every control and bidi code
 * point visible. No redaction (R-4).
 *
 * Callers: every pane that draws a `name` or `displayName` the console did not write, as a child,
 * a `title` or an `aria-label`, and {@link displayUntrustedField} for a key.
 *
 * Input: any string. Output: one line of at most `maxChars` code points of the source and the
 * marker when cut. U+200C and U+200D stay, because real names in several scripts need them (R-8);
 * every other code point of the escape class shows as `<U+XXXX>`, except the emoji carve-outs.
 *
 * Errors: a `maxChars` that is not a positive integer throws a `RangeError` (a programmer error).
 *
 * Pure: no I/O, no global state. A second pass over output that fits returns it.
 */
export function displayUntrustedName(text: string, options: DisplayUntrustedNameOptions = {}): string {
  const { body, marker } = cap(text, checkedCap('displayUntrustedName', options.maxChars ?? NAME_MAX_CHARS));
  return escapeInvisible(oneLine(body), true) + marker;
}

/**
 * Show an author description in the console: {@link displayUntrustedName} with the cap
 * {@link UNTRUSTED_TEXT_MAX_CHARS} (R-1, R-12). No redaction.
 *
 * Callers: every pane that draws a `description` the console did not write.
 *
 * Input: any string. Output: one line of at most 4000 code points of the source and the marker
 * when cut. Pure; a second pass over output that fits returns it.
 */
export function displayUntrustedDescription(text: string): string {
  return displayUntrustedName(text, { maxChars: UNTRUSTED_TEXT_MAX_CHARS });
}

/**
 * Is the text empty to a reader? It holds only white space and code points of the escape class
 * (U+200B, U+200C, U+200D, U+2060, U+FEFF, U+2028, U+2029 and the rest), so nothing would show
 * but escape tokens (R-13).
 *
 * Callers: the six description sites of R-13 (AppCatalogPane, AppOverviewPane, GroupPane,
 * PlatformChannelsPane, PrivateChannelPane, PrivateChannelsPane), which show their empty state in
 * place of such a text. The alert rule and DCR description sites do not call it. Input: any
 * string, the empty string included (`true`). Output: `true` when no code point is visible ink. Pure.
 */
export function isBlankText(text: string): boolean {
  return Array.from(text).every((ch) => /\s/u.test(ch) || isEscaped(ch));
}

/** The key words that end a secret field key, after {@link normalizedFieldKey} (R-13). */
const SECRET_FIELD_KEY_ENDS = /(?:password|passwd|pwd|secret|token|apikey|credentials?|authorization)$/;

/** A field key lower-cased with `-`, `_`, `.` and white space removed: `x-api-key` is `xapikey`. */
function normalizedFieldKey(key: string): string {
  return key.toLowerCase().replace(/[-_.\s]/g, '');
}

/**
 * Does a structured log field key name a secret? The key matches by suffix, any case, so
 * `accessToken`, `clientSecret`, `dbPassword` and `refresh_token` are secret and `tokenCount` is
 * not (R-13).
 *
 * Callers: {@link displayUntrustedField}, and `AppLogRowDetail`, which hides those values in the raw
 * line from 4 characters up. Input: any key. Output: `true` when the normalized key ends in
 * `password`, `passwd`, `pwd`, `secret`, `token`, `apikey`, `credential`, `credentials` or
 * `authorization`. Pure.
 */
export function isSecretFieldKey(key: string): boolean {
  return SECRET_FIELD_KEY_ENDS.test(normalizedFieldKey(key));
}

/**
 * Show one key and value of a structured log line. The value pattern rules of
 * {@link displayUntrusted} cannot see the key of a field, so this helper reads it (R-12).
 *
 * Caller: `AppLogRowDetail`.
 *
 * Input: any key and value. Output: the key through {@link displayUntrustedName}. The value is
 * {@link REDACTED_TEXT} whole when {@link isSecretFieldKey} holds; else the value through
 * {@link displayUntrusted} with the block cap {@link UNTRUSTED_BLOCK_MAX_CHARS}.
 *
 * Pure: no I/O, no global state.
 */
export function displayUntrustedField(key: string, value: string): DisplayedField {
  return {
    key: displayUntrustedName(key),
    value: isSecretFieldKey(key) ? REDACTED_TEXT : displayUntrusted(value, { maxChars: UNTRUSTED_BLOCK_MAX_CHARS }),
  };
}

/** A cap must be a positive integer; any other value is a programmer error of `helper`'s caller. */
function checkedCap(helper: string, maxChars: number): number {
  if (!Number.isInteger(maxChars) || maxChars < 1) {
    throw new RangeError(`${helper}: maxChars must be a positive integer, got ${maxChars}`);
  }
  return maxChars;
}

function oneLine(text: string): string {
  return text.replace(/[\n\r\t]+/g, ' ');
}

// ── Cap ──────────────────────────────────────────────────────────────────────────────────────────

/** Split `text` into the kept body and the marker, cutting after `maxChars` code points. */
function cap(text: string, maxChars: number): { readonly body: string; readonly marker: string } {
  let total = 0;
  let cutAt = text.length;
  let index = 0;
  while (index < text.length) {
    if (total === maxChars) cutAt = index;
    index += isPairAt(text, index) ? 2 : 1;
    total += 1;
  }
  if (total <= maxChars) return { body: text, marker: '' };
  return { body: text.slice(0, cutAt), marker: untrustedTextMarker(maxChars, total) };
}

/** Does a whole surrogate pair start at `index`? */
function isPairAt(text: string, index: number): boolean {
  const unit = text.charCodeAt(index);
  const next = text.charCodeAt(index + 1);
  return unit >= 0xd800 && unit <= 0xdbff && next >= 0xdc00 && next <= 0xdfff;
}

// ── Redact ───────────────────────────────────────────────────────────────────────────────────────

/** One redaction rule: a global pattern and the replacer that keeps a key and its separator. */
interface RedactionRule {
  readonly id: string;
  readonly pattern: RegExp;
  readonly replace: (match: string, ...groups: string[]) => string;
}

const keepPrefix = (_match: string, prefix: string): string => `${prefix}${REDACTED_TEXT}`;

/**
 * A lookahead that captures the head of {@link untrustedTextMarker} where a P2 or P7 value starts.
 * It always matches; its group is `undefined` when no marker head follows.
 */
const MARKER_HEAD_AHEAD = String.raw`(?=(\[Shortened for display, not by the platform)?)`;

/**
 * The idempotency guard of P2 and P7 (R-13): the value is exactly {@link REDACTED_TEXT}, or it
 * starts the cut marker of an earlier pass (`markerHead` holds the marker head). `[hunter22]` is
 * neither, so it goes.
 */
function isEarlierOutput(value: string, markerHead: string | undefined): boolean {
  return value === REDACTED_TEXT || markerHead !== undefined;
}

/** The replacer of P2: keep the prefix and redact the value, unless {@link isEarlierOutput}. */
const keepPrefixUnlessEarlier = (match: string, prefix: string, markerHead: string | undefined, value: string): string =>
  isEarlierOutput(value, markerHead) ? match : `${prefix}${REDACTED_TEXT}`;

/** The source of P7: the lead (group 1), the key part (group 2), the key (group 3), the marker head (group 4), the value (group 5). */
const SECRET_PAIR_SOURCE =
  String.raw`(^|[^A-Za-z])((pass(?:word|wd)?|pwd|secret|token|api[_-]?key|credentials?)["']?\s*[:=]\s*["']?)` +
  MARKER_HEAD_AHEAD +
  String.raw`([^\s"',;}&]+)`;

/**
 * URI userinfo with a password: a scheme of at most 32 characters and `//` (group 1), then `user:password`
 * (group 2) up to the last `@` before the authority ends at `/`, `?`, `#` or white space. The user may be
 * empty, and the password may hold `@` and `:`. Linear: the userinfo class excludes the authority ends, and
 * the greedy run gives the last `@` back once.
 */
const URI_USERINFO_PAIR = /([a-z][a-z0-9+.-]{0,31}:\/\/)([^\s/?#@:]*:[^\s/?#]*)@/dgi;

/**
 * The pair and key shapes, run in this order after the PEM walk (P9) and the self-named rules (P1,
 * P3 to P6), and before the opaque-token scan (P11). No pattern has a nested quantifier or an
 * overlapping alternation, and the URI scheme run is bounded at 32 characters.
 */
const REDACTION_RULES: readonly RedactionRule[] = [
  /* P10: kube config key data. The key name is the guard. */
  {
    id: 'P10',
    pattern: /((?:certificate-authority-data|client-certificate-data|client-key-data):\s*)\S+/gi,
    replace: keepPrefix,
  },
  /*
   * P2: every Authorization value, its scheme kept (`AUTH_SCHEME_SHAPE` of sdk-node). The key is the guard
   * (R-12). A quote may open the value after the scheme, so `Authorization: Bearer "<token>"` loses the token
   * and not the scheme (review row WS-14-P3-R35).
   */
  {
    id: 'P2',
    pattern: new RegExp(
      String.raw`(authorization["']?\s*[:=]\s*["']?(?:[A-Za-z][\w!#$%&'*+.^\`|~-]*\s+["']?)?)` + MARKER_HEAD_AHEAD + String.raw`([^\s"',;]+)`,
      'gi',
    ),
    replace: keepPrefixUnlessEarlier,
  },
  /*
   * P8: URI userinfo, `user:password` up to the last `@` of the authority. The user may be empty
   * (`https://:secret@host`, review row WS-14-P3-R27), and the password may hold a raw `@`. The authority
   * ends at `/`, `?`, `#` or white space, so `http://host:443/a/b@c.d` and a query that holds `:` and `@`
   * stay.
   */
  {
    id: 'P8',
    pattern: URI_USERINFO_PAIR,
    replace: (_match, scheme: string) => `${scheme}${REDACTED_TEXT}@`,
  },
  /* P7: a secret pair. The value guards are in `isSecretPair`. */
  {
    id: 'P7',
    pattern: new RegExp(SECRET_PAIR_SOURCE, 'gi'),
    replace: (match, lead: string, prefix: string, key: string, markerHead: string | undefined, value: string) =>
      !isEarlierOutput(value, markerHead) &&
      isSecretPair(key, value, isTokenUnit(lead.charCodeAt(0)))
        ? `${lead}${prefix}${REDACTED_TEXT}`
        : match,
  },
];

/** The Kubernetes name shape of sdk-node: lowercase runs joined by single hyphens, one or more. */
const K8S_NAME_SHAPE = /^[a-z0-9]+(?:-[a-z0-9]+)+$/;

/** One `<U+XXXX>` escape token, wherever it sits. */
const ESCAPE_TOKEN = /<U\+[0-9A-F]{4,6}>/g;

/**
 * The P7 value guard: 6 or more characters with a digit or a non-letter, or 16 or more characters.
 * `<U+XXXX>` tokens do not count, so an escaped mark never makes a value look secret. So
 * `token: invalid` and `token_expired=true` stay, and `password=hunter2` goes.
 */
function isSecretValue(value: string): boolean {
  const bare = value.replace(ESCAPE_TOKEN, '');
  if (bare.length >= 16) return true;
  return bare.length >= 6 && /[^A-Za-z]/.test(bare);
}

/**
 * The P7 value guards, after {@link isEarlierOutput}. A Kubernetes-shaped value after the bare
 * lowercase key `secret` is a Secret name (R-12). `joined` says a token unit sits before the key, as
 * in `client_secret`.
 */
function isSecretPair(key: string, value: string, joined: boolean): boolean {
  if (key === 'secret' && !joined && K8S_NAME_SHAPE.test(value)) return false;
  return isSecretValue(value);
}

/** P7 with indices: the lead (group 1), the key part (group 2), the key (group 3) and the value (group 5). */
const SECRET_PAIR_RANGES = new RegExp(SECRET_PAIR_SOURCE, 'dgi');

/**
 * The ranges of the value of each secret pair of P7 on `visible` (`password=<value>`, `api_key: <value>`):
 * the values that {@link isSecretPair} holds. Caller: `maskCredentialShapes` of `credential-mask.ts`, so a
 * pair in a catalogue name, or in a server text that the cap would cut, goes before the cap (review row
 * WS-14-P3-R54). Pure, linear in the length of `visible`.
 */
export function secretPairRanges(visible: string): VisibleRange[] {
  const ranges: VisibleRange[] = [];
  for (const match of visible.matchAll(SECRET_PAIR_RANGES)) {
    const [lead, , key, , value] = match.slice(1) as [string, string, string, string | undefined, string];
    const span = (match.indices as RegExpIndicesArray)[5] as [number, number];
    if (isSecretPair(key, value, isTokenUnit(lead.charCodeAt(0)))) ranges.push({ start: span[0], end: span[1] });
  }
  return ranges;
}

/**
 * The range of each PEM private key on `visible`, as P9 finds it: from `-----BEGIN <label>PRIVATE KEY-----`
 * to the end of the next END line, or to the end of the text when no END line follows. Caller:
 * `maskCredentialShapes` of `credential-mask.ts`, so a key in a catalogue name goes too (review row
 * WS-14-P3-R54). Pure, an `indexOf` walk.
 */
export function privateKeyBlockRanges(visible: string): VisibleRange[] {
  const ranges: VisibleRange[] = [];
  let cursor = 0;
  for (;;) {
    const begin = visible.indexOf(PEM_BEGIN, cursor);
    if (begin < 0) return ranges;
    const labelEnd = visible.indexOf(PEM_DELIM, begin + PEM_BEGIN.length);
    if (labelEnd < 0) return ranges;
    if (!isPrivateKeyLabel(visible.slice(begin + PEM_BEGIN.length, labelEnd))) {
      cursor = labelEnd;
      continue;
    }
    const end = visible.indexOf(PEM_END, labelEnd + PEM_DELIM.length);
    const endLabelEnd = end < 0 ? -1 : visible.indexOf(PEM_DELIM, end + PEM_END.length);
    if (endLabelEnd < 0) {
      ranges.push({ start: begin, end: visible.length });
      return ranges;
    }
    cursor = endLabelEnd + PEM_DELIM.length;
    ranges.push({ start: begin, end: cursor });
  }
}

function redactSecrets(text: string): string {
  let out = redactSelfNamedCredentials(redactPemBlocks(text), { context: 'text' });
  for (const rule of REDACTION_RULES) out = out.replace(rule.pattern, rule.replace);
  return redactOpaqueTokens(out);
}

/**
 * Where a self-named rule reads. `'text'`: error text, a reason, a log line, where P4 to P6 have no start
 * guard, so a key that follows a letter of any script still goes (review row WS-14-P2-R26). `'name'`: a name
 * or a display name, where a false hit hides which object the row is (R-4), so P3 does not fire right after a
 * letter or a digit of any script. P4 to P6 fire in both contexts whatever precedes the prefix: `GOCSPX-`,
 * `ya29.` and `AIza` with 35 key characters name no word (review row WS-14-P3-R53).
 */
export type SelfNamedContext = 'text' | 'name';

/** The options of {@link redactSelfNamedCredentials}. */
export interface SelfNamedOptions {
  /** Where the text is read. Default `'name'`, the house rule for a string that names an object. */
  readonly context?: SelfNamedContext;
}

/**
 * The most token characters one P3 match reads as its value: 256. A start that the scan rejects moves on by
 * one code unit, so an unbounded value made a glued run of `Bearer` words quadratic: 1.1 s at 65,532
 * characters and 12.7 s at 180,000 (review rows R1-160 and R1-32). With the bound, each start reads at most
 * 262 code units, so the scan is linear. A value that runs past the bound is no plain words, so P3 masks its
 * whole run ({@link bearerRanges}).
 */
const BEARER_VALUE_SCAN_MAX = 256;

/**
 * P3: `Bearer` in any letter case (each letter spelt as a pair, so no Unicode case fold reaches `ſ` or the
 * Kelvin sign, review row WS-14-P3-R20), a separator, an optional quote (review row WS-14-P3-R35), and a
 * value of 8 to {@link BEARER_VALUE_SCAN_MAX} token characters (group 3). The separator is white space
 * (group 1), or a quote, or an invisible part that the view removed (review rows WS-14-P3-R56 and R59).
 */
const BEARER = new RegExp(`[Bb][Ee][Aa][Rr][Ee][Rr](\\s*)(["']?)([A-Za-z0-9._~+/=-]{8,${BEARER_VALUE_SCAN_MAX}})`, 'dg');

/** `true` for a code unit of a P3 value: `[A-Za-z0-9._~+/=-]`. */
function isBearerValueUnit(unit: number): boolean {
  return (
    (unit >= 0x41 && unit <= 0x5a) ||
    (unit >= 0x61 && unit <= 0x7a) ||
    (unit >= 0x30 && unit <= 0x39) ||
    unit === 0x2e ||
    unit === 0x5f ||
    unit === 0x7e ||
    unit === 0x2b ||
    unit === 0x2f ||
    unit === 0x3d ||
    unit === 0x2d
  );
}

/** A letter or a digit of any script, the character the name context refuses right before `Bearer`. */
const LETTER_OR_DIGIT = /^[\p{L}\p{N}]$/u;

/**
 * `true` for a Bearer value of plain words in `context`, which P3 keeps. The value holds letters, `.` and
 * `-` alone. In error text each part between `.` and `-` is a capitalised word (`Services`,
 * `Cross-Border`), so `Bearer Services Group is unavailable` keeps its subject (review row WS-14-P3-R26),
 * while a lower-case or mixed-case value (`abcdefgh`, `aBcDeFgH`) still goes. In a name one part may also
 * be a lower-case word or capitals alone (`Bearer Consolidated`, R-4), and each part of a joined value is a
 * capitalised word or capitals alone (`Cross-Border Holdings`, review row WS-14-P3-R41); a value of mixed
 * case such as `aBcDeFgH`, or of lower-case parts joined by `.`, goes.
 */
function isPlainWords(value: string, context: SelfNamedContext): boolean {
  if (!/^[A-Za-z.-]+$/.test(value)) return false;
  const parts = value.split(/[.-]/).filter((part) => part !== '');
  if (context === 'text') return parts.every((part) => /^[A-Z][a-z]+$/.test(part));
  if (parts.length === 1) return /^(?:[A-Z]?[a-z]+|[A-Z]+)$/.test(value);
  return parts.every((part) => /^(?:[A-Z][a-z]+|[A-Z]+)$/.test(part));
}

/**
 * The ranges of each P3 value on `visible` in `context`. A rejected start scans on from the next code unit,
 * and each start reads at most {@link BEARER_VALUE_SCAN_MAX} value units, so the scan is linear in the length
 * of `visible`. A value whose token run goes on past the bound is masked to the end of its run, whatever
 * its words: a run of more than 256 token characters with no space is no plain words. A masked range moves
 * the scan to its end.
 */
function bearerRanges(visible: string, context: SelfNamedContext, boundaries: VisibleBoundaries): VisibleRange[] {
  const ranges: VisibleRange[] = [];
  const pattern = new RegExp(BEARER.source, BEARER.flags);
  for (let match = pattern.exec(visible); match !== null; match = pattern.exec(visible)) {
    const gap = String(match[1]);
    const quote = String(match[2]);
    const value = String(match[3]);
    const valueSpan = (match.indices as RegExpIndicesArray)[3] as [number, number];
    const separated = gap !== '' || quote !== '' || boundaries.boundaryBefore(valueSpan[0]);
    const guarded =
      context === 'name' && LETTER_OR_DIGIT.test(String.fromCodePoint(visible.codePointAt(match.index - 1) ?? 0x20)) && !boundaries.boundaryBefore(match.index);
    const longRun = isBearerValueUnit(visible.charCodeAt(valueSpan[1]));
    if (separated && !guarded && (longRun || !isPlainWords(value, context))) {
      let end = valueSpan[1];
      while (isBearerValueUnit(visible.charCodeAt(end))) end += 1;
      ranges.push({ start: valueSpan[0], end });
      pattern.lastIndex = end;
    } else pattern.lastIndex = match.index + 1;
  }
  return ranges;
}

/**
 * The self-named rules P4 to P6, after the JWT scan (P1) and the Bearer scan (P3). Each shape starts with its
 * own prefix, so a match is a credential: P4 a Google OAuth client secret, P5 a Google access token, P6 a
 * Google API key (the prefix and 35 key characters). No pattern has a nested quantifier. Both contexts read
 * the same rules.
 */
const GOOGLE_KEY_RULES: readonly RegExp[] = [/GOCSPX-[A-Za-z0-9_-]{20,}/g, /ya29\.[A-Za-z0-9._-]{20,}/g, /AIza[0-9A-Za-z_-]{35}/g];

/**
 * The ranges of every self-named credential on the visible view `visible` (`visible-text.ts`): each JWT
 * (P1), each Bearer value of `context` (P3), and each match of P4 to P6. `boundaries` says where the view
 * removed an invisible part, so such a part separates as a space does. Ranges may
 * overlap; the caller's `replaceVisibleRanges` joins them.
 *
 * Callers: {@link redactSelfNamedCredentials}, and `maskCredentialShapes` of `credential-mask.ts`, which
 * adds its own ranges on the same view. Pure, linear in the length of `visible` for P1, P3 and P4 to P6.
 */
export function selfNamedCredentialRanges(
  visible: string,
  context: SelfNamedContext,
  boundaries: VisibleBoundaries,
): VisibleRange[] {
  const ranges = [...jwtRanges(visible, boundaries), ...bearerRanges(visible, context, boundaries)];
  for (const rule of GOOGLE_KEY_RULES) ranges.push(...matchRanges(visible, rule));
  return ranges;
}

/**
 * `text` with each self-named credential shape replaced by {@link REDACTED_TEXT}: a JWT (P1), a
 * Bearer token with its scheme kept (P3), a Google OAuth client secret (P4), a Google access token
 * (P5) and a Google API key (P6). Each shape starts with its own prefix (`eyJ`, `Bearer `, `GOCSPX-`,
 * `ya29.`, `AIza`), so a match is a credential and never the name of an object. The pair rules, the
 * URI rule and the opaque-token rule stay out: a false hit in a name hides which object the row is
 * (R-4).
 *
 * Callers: the display scrub of {@link displayUntrusted} with `context: 'text'`, and
 * `maskCredentialShapes` in `credential-mask.ts` with the context of its text, on a catalogue name, the
 * data plane name or server text before any cap, so the cap never leaves a fragment of a matched
 * credential.
 *
 * The rules read the visible view of `visible-text.ts`, so an invisible code point anywhere in a
 * credential, its prefix and the gap after it included, cannot split it, and an invisible code point in
 * place of a space still separates. The JWT scan does not fire right after an ASCII letter (`keyeyJ…` is
 * a word), unless an invisible part sits between them, and fires after `_`, `-` or a digit (review rows
 * WS-14-P2-R41 and WS-14-P3-R58). A Bearer value of plain words stays in both contexts.
 *
 * Input: any string, and the context (default `'name'`, see {@link SelfNamedContext}). Output: the same
 * string with each match replaced. No cap, no layout and no escape. Pure, linear in the length of `text`,
 * and a second pass returns its input.
 */
export function redactSelfNamedCredentials(text: string, options: SelfNamedOptions = {}): string {
  const context = options.context ?? 'name';
  return replaceVisibleRanges(text, (visible, boundaries) => selfNamedCredentialRanges(visible, context, boundaries), REDACTED_TEXT);
}

/**
 * P1: each JWT of `visible`, the shape of `JWT_RE` in sdk-node: `eyJ` with no ASCII letter right before
 * it (an invisible part between them counts as a separator), a segment of 4 or more `[A-Za-z0-9_-]`, a dot, a second such segment, a dot and an optional third
 * segment. A hand scan, linear: when a start fails, every later start inside the same first segment fails
 * the same way, so the scan skips to that segment's end. The regex form took 5 s on 65536 characters of
 * `eyJ-`.
 */
function jwtRanges(visible: string, boundaries: VisibleBoundaries): VisibleRange[] {
  const ranges: VisibleRange[] = [];
  let index = visible.indexOf('eyJ');
  while (index >= 0) {
    /*
     * A letter before `eyJ` fails this start only: a later `eyJ` in the same run can still match. An
     * invisible part between the letter and `eyJ` separates them (review row WS-14-P3-R58).
     */
    const glued = isAsciiLetter(visible.charCodeAt(index - 1)) && !boundaries.boundaryBefore(index);
    const scan = glued ? { jwt: false, next: index + 1 } : scanJwt(visible, index);
    if (scan.jwt) ranges.push({ start: index, end: scan.next });
    index = visible.indexOf('eyJ', scan.next);
  }
  return ranges;
}

/**
 * Read a JWT at `start`. A match gives its end as `next`. A miss gives the end of the first segment,
 * because every later start inside that segment meets the same dot and second segment and fails too.
 */
function scanJwt(text: string, start: number): { readonly jwt: boolean; readonly next: number } {
  const firstEnd = tokenRunEnd(text, start + 3);
  const miss = { jwt: false, next: firstEnd };
  if (firstEnd - (start + 3) < 4 || text[firstEnd] !== '.') return miss;
  const secondEnd = tokenRunEnd(text, firstEnd + 1);
  if (secondEnd - (firstEnd + 1) < 4 || text[secondEnd] !== '.') return miss;
  return { jwt: true, next: tokenRunEnd(text, secondEnd + 1) };
}

/** The index after the run of `[A-Za-z0-9_-]` that starts at `from`. */
function tokenRunEnd(text: string, from: number): number {
  let end = from;
  while (end < text.length && isTokenUnit(text.charCodeAt(end))) end += 1;
  return end;
}

/** An ASCII letter. `NaN` (before the start) is none. */
function isAsciiLetter(unit: number): boolean {
  return (unit >= 0x41 && unit <= 0x5a) || (unit >= 0x61 && unit <= 0x7a);
}

const PEM_BEGIN = '-----BEGIN ';
const PEM_END = '-----END ';
const PEM_DELIM = '-----';

/**
 * P9: a PEM private key, from `-----BEGIN <label>PRIVATE KEY-----` up to the next END line, or to the
 * end of the text when no END line follows. An `indexOf` walk, never a lazy regex span.
 */
function redactPemBlocks(text: string): string {
  let out = '';
  let cursor = 0;
  for (;;) {
    const begin = text.indexOf(PEM_BEGIN, cursor);
    if (begin < 0) break;
    const labelEnd = text.indexOf(PEM_DELIM, begin + PEM_BEGIN.length);
    if (labelEnd < 0) break;
    if (!isPrivateKeyLabel(text.slice(begin + PEM_BEGIN.length, labelEnd))) {
      out += text.slice(cursor, labelEnd);
      cursor = labelEnd;
      continue;
    }
    out += text.slice(cursor, begin) + REDACTED_TEXT;
    const end = text.indexOf(PEM_END, labelEnd + PEM_DELIM.length);
    const endLabelEnd = end < 0 ? -1 : text.indexOf(PEM_DELIM, end + PEM_END.length);
    if (endLabelEnd < 0) return out;
    cursor = endLabelEnd + PEM_DELIM.length;
  }
  return out + text.slice(cursor);
}

function isPrivateKeyLabel(label: string): boolean {
  return label.endsWith('PRIVATE KEY') && /^[A-Z ]*$/.test(label);
}

/** The floors of sdk-node: a run of 40 or more characters with 3.0 bits per character or more. */
const MIN_OPAQUE_RUN_LENGTH = 40;
const MIN_OPAQUE_BITS_PER_CHAR = 3.0;
const DIGEST_PREFIX = 'sha256:';
/** The most characters a run may hold before or after its UUID and still be an identifier (R-12). */
const MAX_UUID_AFFIX_CHARS = 16;
const UUID_LENGTH = 36;
/** A lowercase snake_case or kebab identifier: runs of `[a-z0-9]` joined by single `-` or `_` (R-13). */
const LOWER_JOINED_SHAPE = /^[a-z0-9]+(?:[-_][a-z0-9]+)+$/;
/** A UUID, 8-4-4-4-12 hex in either case, anchored where the scan tries it. */
const UUID_AT = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/iy;

/**
 * P11: an opaque token, found by a hand scan of `[A-Za-z0-9_-]` runs. These runs stay:
 *
 * - hex alone (a git sha or an image digest, which the console shows on purpose), or after `sha256:`;
 * - a UUID with 16 or fewer characters before it and after it in the run: `org_<uuid>` (R-12);
 * - a run with no digit, which is a word or a joined name and not a random token (R-13);
 * - lowercase letters and digits joined by `-` or `_`: a snake_case or kebab identifier (R-13), which
 *   holds the Kubernetes name shape, so a long pod or app name stays (R-2).
 *
 * 40 is above a UUID (36) and a trace id (32).
 */
function redactOpaqueTokens(text: string): string {
  let out = '';
  let runStart = -1;
  for (let index = 0; index <= text.length; index += 1) {
    const inRun = index < text.length && isTokenUnit(text.charCodeAt(index));
    if (inRun) {
      if (runStart < 0) runStart = index;
      continue;
    }
    if (runStart >= 0) {
      const run = text.slice(runStart, index);
      out += isOpaqueToken(run, text.slice(Math.max(0, runStart - DIGEST_PREFIX.length), runStart))
        ? REDACTED_TEXT
        : run;
      runStart = -1;
    }
    if (index < text.length) out += text[index];
  }
  return out;
}

function isTokenUnit(unit: number): boolean {
  return (
    (unit >= 0x30 && unit <= 0x39) ||
    (unit >= 0x41 && unit <= 0x5a) ||
    (unit >= 0x61 && unit <= 0x7a) ||
    unit === 0x5f ||
    unit === 0x2d
  );
}

function isOpaqueToken(run: string, before: string): boolean {
  if (run.length < MIN_OPAQUE_RUN_LENGTH) return false;
  if (/^[0-9a-fA-F]+$/.test(run) || before === DIGEST_PREFIX) return false;
  /* The joined shape holds the Kubernetes name shape of R-2. */
  if (!/[0-9]/.test(run) || LOWER_JOINED_SHAPE.test(run) || isAffixedUuid(run)) return false;
  return shannonEntropy(run) >= MIN_OPAQUE_BITS_PER_CHAR;
}

/**
 * Does the run hold a UUID with at most {@link MAX_UUID_AFFIX_CHARS} characters before and after it?
 * The scan tries each start from 0 to 16, so its cost does not grow with the run.
 */
function isAffixedUuid(run: string): boolean {
  const lastStart = Math.min(MAX_UUID_AFFIX_CHARS, run.length - UUID_LENGTH);
  for (let start = 0; start <= lastStart; start += 1) {
    UUID_AT.lastIndex = start;
    if (UUID_AT.test(run) && run.length - (start + UUID_LENGTH) <= MAX_UUID_AFFIX_CHARS) return true;
  }
  return false;
}

/** Shannon entropy in bits per character of an ASCII run. */
function shannonEntropy(run: string): number {
  const counts = new Map<string, number>();
  for (const ch of run) counts.set(ch, (counts.get(ch) ?? 0) + 1);
  let bits = 0;
  for (const count of counts.values()) {
    const p = count / run.length;
    bits -= p * Math.log2(p);
  }
  return bits;
}

// ── Escape ───────────────────────────────────────────────────────────────────────────────────────

/**
 * The escape class: `\p{Cc}` (C0, U+007F, C1), `\p{Cf}` (the bidi overrides and isolates, zero-width
 * marks, U+00AD, U+FEFF, the tag characters), `\p{Zl}`, `\p{Zp}`, and the blank-looking Hangul
 * fillers U+115F, U+1160, U+3164 and U+FFA0 and the Braille blank U+2800. `isEscaped` adds the marks
 * U+FE00 to U+FE0F, U+E0100 to U+E01EF and U+034F, because a combining mark inside a class is a lint error. `\n` and `\t`
 * reach it only on a multi-line surface, where they stay.
 */
const ESCAPE_CLASS = /[\p{Cc}\p{Cf}\p{Zl}\p{Zp}\u115F\u1160\u3164\uFFA0\u2800]/u;
const PICTOGRAPHIC = /\p{Extended_Pictographic}/u;
const EMOJI_MODIFIER = /\p{Emoji_Modifier}/u;
const ZWNJ = '\u200C';
const ZWJ = '\u200D';
const VS16 = '\uFE0F';

/** Escape the class; `keepJoiners` keeps U+200C and U+200D anywhere, for a name (R-8). */
function escapeInvisible(text: string, keepJoiners: boolean): string {
  const chars = Array.from(text);
  return chars
    .map((ch, index) => (isKept(chars, index, keepJoiners) ? ch : codePointToken(ch)))
    .join('');
}

function isKept(chars: readonly string[], index: number, keepJoiners: boolean): boolean {
  const ch = chars[index] as string;
  if (ch === '\n' || ch === '\t' || !isEscaped(ch)) return true;
  if (keepJoiners && (ch === ZWNJ || ch === ZWJ)) return true;
  return isEmojiPart(chars, index);
}

/**
 * Is `ch` in the escape class: {@link ESCAPE_CLASS}, U+FE00 to U+FE0F, U+034F, or a variation selector of
 * the supplement U+E0100 to U+E01EF, which a browser draws as nothing (review row WS-14-P3-R57)?
 */
function isEscaped(ch: string): boolean {
  const point = Number(ch.codePointAt(0));
  return ESCAPE_CLASS.test(ch) || point === 0x034f || (point >= 0xfe00 && point <= 0xfe0f) || (point >= 0xe0100 && point <= 0xe01ef);
}

/**
 * The carve-outs that keep emoji whole: U+FE0F right after a pictograph, and U+200D between two
 * pictographs. The pictograph before a U+200D may carry its U+FE0F or a skin tone modifier.
 */
function isEmojiPart(chars: readonly string[], index: number): boolean {
  const ch = chars[index];
  if (ch === VS16) return isPictograph(chars[index - 1]);
  if (ch !== ZWJ || !isPictograph(chars[index + 1])) return false;
  const before = chars[index - 1];
  if (isPictograph(before)) return true;
  return (before === VS16 || (before !== undefined && EMOJI_MODIFIER.test(before))) && isPictograph(chars[index - 2]);
}

function isPictograph(ch: string | undefined): boolean {
  return ch !== undefined && PICTOGRAPHIC.test(ch);
}

function codePointToken(ch: string): string {
  return `<U+${Number(ch.codePointAt(0)).toString(16).toUpperCase().padStart(4, '0')}>`;
}
